import {
  DefinitionActionKindEnum,
  DefinitionActionSubKindEnum,
  type ActionResponse,
} from '@acme/api';
import { useModal } from '@acme/foundation';
import { Button } from 'flowbite-react';
import { findAction, isShow } from '../../../features/actions/lib';
import type { ActionKind, FormKind, SignKind, UploadKind } from '../model';
import { getConfirmModalFromAction } from './confirmModalByType';

const DEFAULT_CONFIRM_TITLE = 'Подтвердить действие?';
const DEFAULT_CONFIRM_CONTENT = 'Вы уверены?';

type RunConfirmFn = (
  action: ActionKind | undefined,
  run: () => void,
  actionResponse?: ActionResponse | null
) => Promise<void>;

function useRunWithConfirm(): RunConfirmFn {
  const { openConfirmModal } = useModal();
  return async (action, run, actionResponse) => {
    if (!action?.handler) return;
    const customModal = actionResponse
      ? getConfirmModalFromAction(actionResponse)
      : null;
    const needConfirm = action.confirmModal ?? customModal;
    if (needConfirm) {
      const title =
        customModal?.title ??
        action.confirmModal?.title ??
        DEFAULT_CONFIRM_TITLE;
      const content =
        customModal?.content ??
        action.confirmModal?.content ??
        DEFAULT_CONFIRM_CONTENT;
      const ok = await openConfirmModal({ title, content });
      if (!ok) return;
    }
    run();
  };
}

type ActionButtonProps = {
  visible: boolean;
  label: string | null | undefined;
  color: 'red' | 'primary' | 'alternative' | 'blue';
  onClick: () => void;
};

function ActionButton({ visible, label, color, onClick }: ActionButtonProps) {
  if (!visible || label == null) return null;
  return (
    <Button color={color} className="whitespace-nowrap" onClick={onClick}>
      {label}
    </Button>
  );
}

export const ApplicationActions = ({
  actions,
  confirm,
  upload,
  sign,
  reject,
  rework,
  delete: deleteAction,
  form,
  confirmDealUpload,
}: {
  actions: Array<ActionResponse> | null;
  applicationId?: string;
  confirm: ActionKind;
  upload?: UploadKind;
  sign?: SignKind;
  reject?: ActionKind;
  rework?: {
    handler: () => void;
    editDraft?: ActionKind;
  };
  delete?: ActionKind;
  form?: Partial<FormKind>;
  confirmDealUpload?: ActionKind;
}) => {
  const runWithConfirm = useRunWithConfirm();

  const uploadConfirmDealAction =
    actions?.find(
      (a) =>
        a.kind === DefinitionActionKindEnum.Upload &&
        (a.subKind === null || a.subKind === undefined)
    ) ?? null;

  const confirmAction = findAction(actions, DefinitionActionKindEnum.Confirm);

  const signContractAction =
    actions?.find(
      (a) =>
        a.kind === DefinitionActionKindEnum.Sign &&
        (a.subKind === null || a.subKind === undefined)
    ) ?? null;

  return (
    <div className="flex justify-end gap-4">
      <div className="flex gap-4">
        {isShow(
          actions,
          DefinitionActionKindEnum.Form,
          DefinitionActionSubKindEnum.FormEditDraft
        ) &&
          rework?.editDraft?.handler && (
            <ActionButton
              visible
              label={
                findAction(
                  actions,
                  DefinitionActionKindEnum.Form,
                  DefinitionActionSubKindEnum.FormEditDraft
                )?.name
              }
              color="alternative"
              onClick={() =>
                runWithConfirm(rework?.editDraft, () =>
                  rework?.editDraft?.handler()
                )
              }
            />
          )}
        {isShow(actions, DefinitionActionKindEnum.Rework) &&
          rework?.handler && (
            <ActionButton
              visible
              label={findAction(actions, DefinitionActionKindEnum.Rework)?.name}
              color="alternative"
              onClick={() => rework.handler()}
            />
          )}
      </div>
      <div className="flex justify-end gap-4">
        {isShow(actions, DefinitionActionKindEnum.Delete) &&
          deleteAction?.handler && (
            <ActionButton
              visible
              label={findAction(actions, DefinitionActionKindEnum.Delete)?.name}
              color="red"
              onClick={() =>
                runWithConfirm(deleteAction, () => deleteAction?.handler())
              }
            />
          )}
        {isShow(actions, DefinitionActionKindEnum.Reject) &&
          reject?.handler && (
            <ActionButton
              visible
              label={findAction(actions, DefinitionActionKindEnum.Reject)?.name}
              color="red"
              onClick={() => runWithConfirm(reject, () => reject?.handler())}
            />
          )}
        {uploadConfirmDealAction && confirmDealUpload?.handler && (
          <ActionButton
            visible
            label={uploadConfirmDealAction.name}
            color="primary"
            onClick={() =>
              runWithConfirm(confirmDealUpload, () =>
                confirmDealUpload.handler(uploadConfirmDealAction.id)
              )
            }
          />
        )}
        {isShow(
          actions,
          DefinitionActionKindEnum.Upload,
          DefinitionActionSubKindEnum.UploadClientPaymentDoc
        ) &&
          upload?.uploadClientPaymentDoc?.handler && (
            <ActionButton
              visible
              label={
                findAction(
                  actions,
                  DefinitionActionKindEnum.Upload,
                  DefinitionActionSubKindEnum.UploadClientPaymentDoc
                )?.name
              }
              color="primary"
              onClick={() =>
                runWithConfirm(upload?.uploadClientPaymentDoc, () =>
                  upload?.uploadClientPaymentDoc?.handler()
                )
              }
            />
          )}
        {isShow(
          actions,
          DefinitionActionKindEnum.Upload,
          DefinitionActionSubKindEnum.UploadAdminToProviderDoc
        ) &&
          upload?.uploadAdminToProviderDoc?.handler && (
            <ActionButton
              visible
              label={
                findAction(
                  actions,
                  DefinitionActionKindEnum.Upload,
                  DefinitionActionSubKindEnum.UploadAdminToProviderDoc
                )?.name
              }
              color="primary"
              onClick={() =>
                runWithConfirm(upload?.uploadAdminToProviderDoc, () =>
                  upload?.uploadAdminToProviderDoc?.handler()
                )
              }
            />
          )}
        {isShow(
          actions,
          DefinitionActionKindEnum.Upload,
          DefinitionActionSubKindEnum.UploadTransferCommessionRecepotDoc
        ) &&
          upload?.uploadTransferCommessionRecepotDoc?.handler && (
            <ActionButton
              visible
              label={
                findAction(
                  actions,
                  DefinitionActionKindEnum.Upload,
                  DefinitionActionSubKindEnum.UploadTransferCommessionRecepotDoc
                )?.name
              }
              color="primary"
              onClick={() =>
                runWithConfirm(upload?.uploadTransferCommessionRecepotDoc, () =>
                  upload?.uploadTransferCommessionRecepotDoc?.handler()
                )
              }
            />
          )}
        {isShow(
          actions,
          DefinitionActionKindEnum.Upload,
          DefinitionActionSubKindEnum.UploadTransferRecepotDoc
        ) &&
          upload?.uploadTransferRecepotDoc?.handler && (
            <ActionButton
              visible
              label={
                findAction(
                  actions,
                  DefinitionActionKindEnum.Upload,
                  DefinitionActionSubKindEnum.UploadTransferRecepotDoc
                )?.name
              }
              color="primary"
              onClick={() =>
                runWithConfirm(upload?.uploadTransferRecepotDoc, () =>
                  upload?.uploadTransferRecepotDoc?.handler()
                )
              }
            />
          )}
        {signContractAction && sign?.signContract?.handler && (
          <ActionButton
            visible
            label={signContractAction.name}
            color="primary"
            onClick={() =>
              runWithConfirm(sign?.signContract, () =>
                sign?.signContract?.handler()
              )
            }
          />
        )}
        {isShow(
          actions,
          DefinitionActionKindEnum.Sign,
          DefinitionActionSubKindEnum.SignExchangeContract
        ) &&
          sign?.signExchangeContract?.handler && (
            <ActionButton
              visible
              label={
                findAction(
                  actions,
                  DefinitionActionKindEnum.Sign,
                  DefinitionActionSubKindEnum.SignExchangeContract
                )?.name
              }
              color="primary"
              onClick={() =>
                runWithConfirm(sign?.signExchangeContract, () =>
                  sign?.signExchangeContract?.handler(
                    findAction(
                      actions,
                      DefinitionActionKindEnum.Sign,
                      DefinitionActionSubKindEnum.SignExchangeContract
                    )?.id
                  )
                )
              }
            />
          )}
        {isShow(
          actions,
          DefinitionActionKindEnum.Sign,
          DefinitionActionSubKindEnum.SignExchangeAct
        ) &&
          sign?.signExchangeAct?.handler && (
            <ActionButton
              visible
              label={
                findAction(
                  actions,
                  DefinitionActionKindEnum.Sign,
                  DefinitionActionSubKindEnum.SignExchangeAct
                )?.name
              }
              color="primary"
              onClick={() =>
                runWithConfirm(sign?.signExchangeAct, () =>
                  sign?.signExchangeAct?.handler(
                    findAction(
                      actions,
                      DefinitionActionKindEnum.Sign,
                      DefinitionActionSubKindEnum.SignExchangeAct
                    )?.id
                  )
                )
              }
            />
          )}
        {isShow(
          actions,
          DefinitionActionKindEnum.Sign,
          DefinitionActionSubKindEnum.SignTransferContract
        ) &&
          sign?.signTransferContract?.handler && (
            <ActionButton
              visible
              label={
                findAction(
                  actions,
                  DefinitionActionKindEnum.Sign,
                  DefinitionActionSubKindEnum.SignTransferContract
                )?.name
              }
              color="primary"
              onClick={() =>
                runWithConfirm(sign?.signTransferContract, () =>
                  sign?.signTransferContract?.handler(
                    findAction(
                      actions,
                      DefinitionActionKindEnum.Sign,
                      DefinitionActionSubKindEnum.SignTransferContract
                    )?.id
                  )
                )
              }
            />
          )}
        {isShow(
          actions,
          DefinitionActionKindEnum.Sign,
          DefinitionActionSubKindEnum.SignTransferAct
        ) &&
          sign?.signTransferAct?.handler && (
            <ActionButton
              visible
              label={
                findAction(
                  actions,
                  DefinitionActionKindEnum.Sign,
                  DefinitionActionSubKindEnum.SignTransferAct
                )?.name
              }
              color="primary"
              onClick={() =>
                runWithConfirm(sign?.signTransferAct, () =>
                  sign?.signTransferAct?.handler(
                    findAction(
                      actions,
                      DefinitionActionKindEnum.Sign,
                      DefinitionActionSubKindEnum.SignTransferAct
                    )?.id
                  )
                )
              }
            />
          )}
        {isShow(
          actions,
          DefinitionActionKindEnum.Form,
          DefinitionActionSubKindEnum.FormEditApplication
        ) &&
          form?.formEditApplication?.handler && (
            <ActionButton
              visible
              label={
                findAction(
                  actions,
                  DefinitionActionKindEnum.Form,
                  DefinitionActionSubKindEnum.FormEditApplication
                )?.name
              }
              color="primary"
              onClick={() =>
                runWithConfirm(form?.formEditApplication, () =>
                  form?.formEditApplication?.handler()
                )
              }
            />
          )}
        {isShow(
          actions,
          DefinitionActionKindEnum.Form,
          DefinitionActionSubKindEnum.FormAgentInfo
        ) &&
          form?.formAgentInfo?.handler && (
            <ActionButton
              visible
              label={
                findAction(
                  actions,
                  DefinitionActionKindEnum.Form,
                  DefinitionActionSubKindEnum.FormAgentInfo
                )?.name
              }
              color="primary"
              onClick={() =>
                runWithConfirm(form?.formAgentInfo, () =>
                  form?.formAgentInfo?.handler()
                )
              }
            />
          )}
        {isShow(
          actions,
          DefinitionActionKindEnum.Form,
          DefinitionActionSubKindEnum.FormCounterpartyInfo
        ) &&
          form?.formCounterpartyInfo?.handler && (
            <ActionButton
              visible
              label={
                findAction(
                  actions,
                  DefinitionActionKindEnum.Form,
                  DefinitionActionSubKindEnum.FormCounterpartyInfo
                )?.name
              }
              color="primary"
              onClick={() =>
                runWithConfirm(form?.formCounterpartyInfo, () =>
                  form?.formCounterpartyInfo?.handler()
                )
              }
            />
          )}
        {isShow(
          actions,
          DefinitionActionKindEnum.Form,
          DefinitionActionSubKindEnum.FormExchangeRates
        ) &&
          form?.formExchangeRates?.handler && (
            <ActionButton
              visible
              label={
                findAction(
                  actions,
                  DefinitionActionKindEnum.Form,
                  DefinitionActionSubKindEnum.FormExchangeRates
                )?.name
              }
              color="primary"
              onClick={() =>
                runWithConfirm(form?.formExchangeRates, () =>
                  form?.formExchangeRates?.handler()
                )
              }
            />
          )}
        {isShow(
          actions,
          DefinitionActionKindEnum.Form,
          DefinitionActionSubKindEnum.FormWalletsWhichTransfer
        ) &&
          form?.formWalletsWhichTransfer?.handler && (
            <ActionButton
              visible
              label={
                findAction(
                  actions,
                  DefinitionActionKindEnum.Form,
                  DefinitionActionSubKindEnum.FormWalletsWhichTransfer
                )?.name
              }
              color="primary"
              onClick={() =>
                runWithConfirm(form?.formWalletsWhichTransfer, () =>
                  form?.formWalletsWhichTransfer?.handler()
                )
              }
            />
          )}
        {isShow(
          actions,
          DefinitionActionKindEnum.Form,
          DefinitionActionSubKindEnum.FormProvideWallets
        ) &&
          form?.formProvideWallets?.handler && (
            <ActionButton
              visible
              label={
                findAction(
                  actions,
                  DefinitionActionKindEnum.Form,
                  DefinitionActionSubKindEnum.FormProvideWallets
                )?.name
              }
              color="primary"
              onClick={() =>
                runWithConfirm(form?.formProvideWallets, () =>
                  form?.formProvideWallets?.handler()
                )
              }
            />
          )}
        {isShow(
          actions,
          DefinitionActionKindEnum.Form,
          DefinitionActionSubKindEnum.FormTransferProvideWallets
        ) &&
          form?.formTransferProvideWallets?.handler && (
            <ActionButton
              visible
              label={
                findAction(
                  actions,
                  DefinitionActionKindEnum.Form,
                  DefinitionActionSubKindEnum.FormTransferProvideWallets
                )?.name
              }
              color="primary"
              onClick={() =>
                runWithConfirm(form?.formTransferProvideWallets, () =>
                  form?.formTransferProvideWallets?.handler()
                )
              }
            />
          )}
        {isShow(
          actions,
          DefinitionActionKindEnum.Form,
          DefinitionActionSubKindEnum.FormTransferFiat
        ) &&
          form?.formTransferFiat?.handler && (
            <ActionButton
              visible
              label={
                findAction(
                  actions,
                  DefinitionActionKindEnum.Form,
                  DefinitionActionSubKindEnum.FormTransferFiat
                )?.name
              }
              color="primary"
              onClick={() =>
                runWithConfirm(form?.formTransferFiat, () =>
                  form?.formTransferFiat?.handler()
                )
              }
            />
          )}
        {isShow(
          actions,
          DefinitionActionKindEnum.Form,
          DefinitionActionSubKindEnum.FormTransactionId
        ) &&
          form?.formTransactionId?.handler && (
            <ActionButton
              visible
              label={
                findAction(
                  actions,
                  DefinitionActionKindEnum.Form,
                  DefinitionActionSubKindEnum.FormTransactionId
                )?.name
              }
              color="primary"
              onClick={() =>
                runWithConfirm(form?.formTransactionId, () =>
                  form?.formTransactionId?.handler()
                )
              }
            />
          )}
        {isShow(actions, DefinitionActionKindEnum.Confirm) &&
          confirm?.handler && (
            <ActionButton
              visible
              label={
                findAction(actions, DefinitionActionKindEnum.Confirm)?.name
              }
              color="primary"
              onClick={() =>
                runWithConfirm(
                  confirm,
                  () => confirm?.handler(confirmAction?.id),
                  confirmAction
                )
              }
            />
          )}
      </div>
    </div>
  );
};
